import * as Maths from "oc-commons/Maths";
import numeric from "numeric";
import { CameraTuple } from "./CameraTuple";
import bezier from "cubic-bezier";

import * as _ from "lodash";
import { CatmullRomCurve3, Vector3 } from "three";

export type BezierFunction = (t: number) => number;
type SplineFunction = (x: number) => number;
type CurveFunction = (t: number) => Vector3;

const epsilon = 1e-5;
export const easeInOutSine: BezierFunction = bezier(
  0.445,
  0.05,
  0.55,
  0.95,
  epsilon,
) as BezierFunction;
export const ease: BezierFunction = bezier(0.73, 0.16, 0.31, 0.93, epsilon) as BezierFunction;
export const easeInOut: BezierFunction = bezier(0.42, 0, 0.58, 1, epsilon) as BezierFunction;

export class CameraPath {
  isLinear?: boolean;
  tuples: CameraTuple[];
  _positionCurve!: CurveFunction;
  _targetCurve!: CurveFunction;
  upCurve!: CurveFunction;

  /** */
  constructor() {
    this.tuples = [];
  }

  /** */
  add(cameraTuple: CameraTuple): void {
    this.tuples.push(cameraTuple.clone());
  }

  /** */
  spline(xArray: number[], yArray: number[]): SplineFunction {
    const s = numeric.spline(xArray, yArray);
    return (x: number): number => s.at(x);
  }

  /** */
  linear(xArray: number[], yArray: number[]): SplineFunction {
    const l = xArray.length - 1;
    return (x: number): number => {
      const index = Maths.cutBetween(0, _.sortedIndex(xArray, x), l);
      const d = xArray[index] < x ? +1 : -1;
      let i0 = Math.min(index, index + d);
      let i1 = Math.max(index, index + d);
      i0 = Maths.cutBetween(0, i0, l);
      i1 = Maths.cutBetween(0, i1, l);
      if (i0 == i1) {
        return yArray[i0];
      }
      const x0 = xArray[i0];
      const x1 = xArray[i1];
      const y0 = yArray[i0];
      const y1 = yArray[i1];
      const f = (x - x0) / (x1 - x0);
      return (1 - f) * y0 + f * y1;
    };
  }

  /** */
  finalize(resolution: number = 1000, wayFunction: BezierFunction = easeInOutSine): void {
    const positionPath = new CatmullRomCurve3(
      this.tuples.map((x: CameraTuple): Vector3 => x.position),
    );

    const acceleratedCurve =
      (path: CatmullRomCurve3): CurveFunction =>
      (t: number): Vector3 => {
        const uu = wayFunction(t);
        const u = Number.isNaN(uu) ? 0 : Math.max(0, Math.min(uu, 1));
        const p = path.getPointAt(u);
        return p;
      };

    this._positionCurve = acceleratedCurve(positionPath);
    this._targetCurve = acceleratedCurve(
      new CatmullRomCurve3(this.tuples.map((x: CameraTuple): Vector3 => x.target)),
    );
    this.upCurve = acceleratedCurve(
      new CatmullRomCurve3(this.tuples.map((x: CameraTuple): Vector3 => x.up)),
    );
  }

  /** */
  getAt(t: number): CameraTuple {
    return new CameraTuple(this._positionCurve(t), this._targetCurve(t), this.upCurve(t));
  }

  /** */
  get positionCurve(): CurveFunction {
    return this._positionCurve;
  }

  /** */
  get targetCurve(): CurveFunction {
    return this._targetCurve;
  }
}
